import {
  ClickedObjectIDs,
  ClickedObjectIDsAfterSearch,
  InsightsEvents,
  ViewedObjectIDs,
  ViewedFilters,
  ClickedFilters,
  AddedToCartObjectIDs,
  AddedToCartObjectIDsAfterSearch,
  ConvertedObjectIDs,
  ConvertedObjectIDsAfterSearch,
} from "@algolia/client-insights";
import { algolia as algoliaClient } from ".";
import { getGuestUserRandomId } from "@/helpers/global";
import { GlobalSearchSectionsType, TAnalyticsFilterEvent } from "@/utils/types/algolia";
import { algoliaConfig } from "./config";

const eventOption = <T extends string, K extends string, P extends string>(
  eventName: T,
  type: K,
  subType?: P
) =>
  ({
    eventName,
    type,
    subType,
  }) as const;

const eventTypes = {
  products: {
    view: eventOption("Product Viewed", "view"),
    click: {
      beforeSearch: eventOption("Product Clicked", "click"),
      afterSearch: eventOption("Product Clicked After Search", "click"),
    },
    addToCart: {
      beforeSearch: eventOption(
        "Product Add To List",
        "conversion",
        "addToCart"
      ),
      afterSearch: eventOption(
        "Product Add To List After Search",
        "conversion",
        "addToCart"
      ),
    },
    conversion: {
      socialShareBeforeSearch: eventOption(
        "Product Shared Before Search",
        "conversion"
      ),
      socialShareAfterSearch: eventOption(
        "Product Shared After Search",
        "conversion"
      ),
      addToFavouriteBeforeSearch: eventOption(
        "Product Added to Favourites Before Search",
        "conversion"
      ),
      addToFavouriteAfterSearch: eventOption(
        "Product Added to Favourites After Search",
        "conversion"
      ),
    },
    filter: {
      view: eventOption("Product Filter Viewed", "view"),
      click: eventOption("Product Filter Clicked", "click"),
    },
  },
  recipes: {
    view: eventOption("Recipe Viewed", "view"),
    click: {
      beforeSearch: eventOption("Recipe Clicked", "click"),
      afterSearch: eventOption("Recipe Clicked After Search", "click"),
    },
    conversion: {
      socialShareBeforeSearch: eventOption(
        "Recipe Shared Before Search",
        "conversion"
      ),
      socialShareAfterSearch: eventOption(
        "Recipe Shared After Search",
        "conversion"
      ),
      addToFavouriteBeforeSearch: eventOption(
        "Recipe Added to Favourites Before Search",
        "conversion"
      ),
      addToFavouriteAfterSearch: eventOption(
        "Recipe Added to Favourites After Search",
        "conversion"
      ),
    },
    filter: {
      view: eventOption("Recipe Filter Viewed", "view"),
      click: eventOption("Recipe Filter Clicked", "click"),
    },
  },
  articles: {
    view: eventOption("Article Viewed", "view"),
    click: {
      beforeSearch: eventOption("Article Clicked", "click"),
      afterSearch: eventOption("Article Clicked After Search", "click"),
    },
    conversion: {
      socialShareBeforeSearch: eventOption(
        "Article Shared Before Search",
        "conversion"
      ),
      socialShareAfterSearch: eventOption(
        "Article Shared After Search",
        "conversion"
      ),
      addToFavouriteBeforeSearch: eventOption(
        "Article Added to Favourites Before Search",
        "conversion"
      ),
      addToFavouriteAfterSearch: eventOption(
        "Article Added to Favourites After Search",
        "conversion"
      ),
    },
    filter: {
      view: eventOption("Article Filter Viewed", "view"),
      click: eventOption("Article Filter Clicked", "click"),
    },
  },
} as const;

export const sendAnalyticsEvents = async (events: InsightsEvents["events"]) => {
  try {
    const response = await algoliaClient.analytics.pushEvents({ events });

    if (response.status !== 200)
      throw new Error(`Failed to capture event,status: ${response.status}`);
  } catch (error) {
    console.error("Unable to Send Analytics:", error);
  }
};

export const algoliaAnalyticsKind = {PRODUCTS:'products', RECIPES:'recipes', ARTICLES:'articles'} as const
export type TAlgoliaAnalyticsKind = Record<keyof typeof algoliaAnalyticsKind, GlobalSearchSectionsType>

export const analyticsViewEvent = (payload: {
  kind: GlobalSearchSectionsType;
  index: string;
  objectIDs: string[];
  userToken?: string;
}) => {
  const { kind, index, objectIDs, userToken } = payload;
  const viewEvent: ViewedObjectIDs = {
    eventName: eventTypes[kind].view.eventName,
    eventType: eventTypes[kind].view.type,
    index: index,
    objectIDs,
    authenticatedUserToken: userToken,
    userToken: getGuestUserRandomId(),
    timestamp: Date.now(),
  };

  void sendAnalyticsEvents([viewEvent]);
};

export const analyticsViewFilterEvent = (payload:TAnalyticsFilterEvent) => {
  const { kind, index, filters, userToken } = payload;
  const viewFilterEvent: ViewedFilters = {
    eventName: eventTypes[kind].filter.view.eventName,
    eventType: eventTypes[kind].filter.view.type,
    index: index,
    filters,
    authenticatedUserToken: userToken,
    userToken: getGuestUserRandomId(),
    timestamp: Date.now(),
  };

  void sendAnalyticsEvents([viewFilterEvent]);
};
export const analyticsFilterClickEvent = (payload: TAnalyticsFilterEvent) => {
  const { kind, index, filters, userToken } = payload;
  const filterClick: ClickedFilters = {
    eventName: eventTypes[kind].filter.click.eventName,
    eventType: eventTypes[kind].filter.click.type,
    index: index,
    filters,
    authenticatedUserToken: userToken,
    userToken: getGuestUserRandomId(),
    timestamp: Date.now(),
  };

  void sendAnalyticsEvents([filterClick]);
};

export const analyticsClickEventBeforeSearch = (payload: {
  kind: GlobalSearchSectionsType;
  index: string;
  objectIDs: string[];
  userToken?: string;
}) => {
  const { kind, index, objectIDs, userToken } = payload;
  const clickEvent: ClickedObjectIDs = {
    eventName: eventTypes[kind].click.beforeSearch.eventName,
    eventType: eventTypes[kind].click.beforeSearch.type,
    index,
    objectIDs,
    authenticatedUserToken: userToken,
    userToken: getGuestUserRandomId(),
    timestamp: Date.now(),
  };

  void sendAnalyticsEvents([clickEvent]);
};

export const analyticsClickEventAfterSearch = (payload: {
  kind: GlobalSearchSectionsType;
  index: string;
  objectIDs: string[];
  userToken?: string;
  positions: number[];
  queryID: string;
}) => {
  const { kind, index, objectIDs, userToken, positions, queryID } = payload;
  const clickEvent: ClickedObjectIDsAfterSearch = {
    eventName: eventTypes[kind].click.afterSearch.eventName,
    eventType: eventTypes[kind].click.afterSearch.type,
    index,
    objectIDs,
    positions,
    queryID,
    authenticatedUserToken: userToken,
    userToken: getGuestUserRandomId(),
    timestamp: Date.now(),
  };

  void sendAnalyticsEvents([clickEvent]);
};

export const analyticsSocialSharedEventBeforeSearch = (payload: {
  kind: GlobalSearchSectionsType;
  index: string;
  objectIDs: string[];
  userToken?: string;
}) => {
  const { kind, index, objectIDs, userToken } = payload;
  const clickEvent: ConvertedObjectIDs = {
    eventName: eventTypes[kind].conversion.socialShareBeforeSearch.eventName,
    eventType: eventTypes[kind].conversion.socialShareBeforeSearch.type,
    index,
    objectIDs,
    authenticatedUserToken: userToken,
    userToken: getGuestUserRandomId(),
    timestamp: Date.now(),
  };

  void sendAnalyticsEvents([clickEvent]);
};

export const analyticsSocialSharedEventAfterSearch = (payload: {
  kind: GlobalSearchSectionsType;
  index: string;
  objectIDs: string[];
  userToken?: string;
  queryID: string;
}) => {
  const { kind, index, objectIDs, userToken, queryID } = payload;
  const clickEvent: ConvertedObjectIDsAfterSearch = {
    eventName: eventTypes[kind].conversion.socialShareAfterSearch.eventName,
    eventType: eventTypes[kind].conversion.socialShareAfterSearch.type,
    index,
    objectIDs,
    queryID,
    authenticatedUserToken: userToken,
    userToken: getGuestUserRandomId(),
    timestamp: Date.now(),
  };

  void sendAnalyticsEvents([clickEvent]);
};

export const analyticsAddToFavouriteEventBeforeSearch = (payload: {
  kind: GlobalSearchSectionsType;
  index: string;
  objectIDs: string[];
  userToken?: string;
}) => {
  const { kind, index, objectIDs, userToken } = payload;
  const clickEvent: ConvertedObjectIDs = {
    eventName: eventTypes[kind].conversion.addToFavouriteBeforeSearch.eventName,
    eventType: eventTypes[kind].conversion.addToFavouriteBeforeSearch.type,
    index,
    objectIDs,
    authenticatedUserToken: userToken,
    userToken: getGuestUserRandomId(),
    timestamp: Date.now(),
  };

  void sendAnalyticsEvents([clickEvent]);
};

export const analyticsAddToFavouriteEventAfterSearch = (payload: {
  kind: GlobalSearchSectionsType;
  index: string;
  objectIDs: string[];
  userToken?: string;
  queryID: string;
}) => {
  const { kind, index, objectIDs, userToken, queryID } = payload;
  const clickEvent: ConvertedObjectIDsAfterSearch = {
    eventName: eventTypes[kind].conversion.addToFavouriteAfterSearch.eventName,
    eventType: eventTypes[kind].conversion.addToFavouriteAfterSearch.type,
    index,
    objectIDs,
    queryID,
    authenticatedUserToken: userToken,
    userToken: getGuestUserRandomId(),
    timestamp: Date.now(),
  };

  void sendAnalyticsEvents([clickEvent]);
};

export const analyticsAddToCartEventBeforeSearch = (payload: {
  objectIDs: string[];
  userToken?: string;
  quantity?: number;
}) => {
  const { objectIDs, userToken, quantity } = payload;
  if (quantity !== undefined && quantity > 0) {
    const AddToCartEvent: AddedToCartObjectIDs = {
      eventName: eventTypes["products"].addToCart.beforeSearch.eventName,
      eventType: eventTypes["products"].addToCart.beforeSearch.type,
      eventSubtype: eventTypes["products"].addToCart.beforeSearch.subType!,
      index: algoliaConfig.indexNames.products,
      objectIDs,
      userToken: getGuestUserRandomId(),
      authenticatedUserToken: userToken,
      objectData: quantity ? [{ quantity }] : undefined,
      timestamp: Date.now(),
    };

    void sendAnalyticsEvents([AddToCartEvent]);
  }
};

export const analyticsAddToCartEventAfterSearch = (payload: {
  objectIDs: string[];
  userToken?: string;
  queryID: string;
  quantity?: number;
}) => {
  const { objectIDs, userToken, queryID, quantity } = payload;
  if (quantity !== undefined && quantity > 0) {
    const AddToCartEvent: AddedToCartObjectIDsAfterSearch = {
      eventName: eventTypes["products"].addToCart.afterSearch.eventName,
      eventType: eventTypes["products"].addToCart.afterSearch.type,
      eventSubtype: eventTypes["products"].addToCart.afterSearch.subType!,
      index: algoliaConfig.indexNames.products,
      queryID,
      objectIDs,
      userToken: getGuestUserRandomId(),
      authenticatedUserToken: userToken,
      objectData: quantity ? [{ quantity }] : undefined,
      timestamp: Date.now(),
    };

    void sendAnalyticsEvents([AddToCartEvent]);
  }
};
